import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Grid,
  Button,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Report } from '../../models/report';
import Accounts from '../../collections/accounts';
import DateRangeFilter from '../common/DateRangeFilter';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';

const CheckInHistory = () => {
  const [reportData, setReportData] = useState([]);
  const [totalWorkHours, setTotalWorkHours] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserRole(decodedToken.role);
    }

    const fetchAccounts = async () => {
      try {
        const accountsCollection = new Accounts();
        await accountsCollection.fetch();
        setAccounts(accountsCollection.items || []);
      } catch (error) {
        console.error("Failed to fetch accounts for filter:", error);
        setAccounts([]); // Ensure accounts is always an array
      }
    };

    if (userRole === 'ADMIN') {
      fetchAccounts();
    }
  }, [userRole]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (startDate) {
        params.start_date = dayjs(startDate).format('YYYY-MM-DD');
      }
      if (endDate) {
        params.end_date = dayjs(endDate).format('YYYY-MM-DD');
      }
      if (selectedAccount) {
        params.account_id = selectedAccount.id;
      }

      const data = await Report.fetch('work_hours', params);
      setReportData(data.items);
      setTotalWorkHours(data.total_work_hours);
    } catch (err) {
      setError('Tải lịch sử điểm danh thất bại.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedAccount]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Lịch sử điểm danh
      </Typography>

      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Tổng giờ công: {totalWorkHours.toFixed(2)}
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
             <TextField
                label="Từ ngày"
                type="date"
                fullWidth
                value={startDate ? dayjs(startDate).format('YYYY-MM-DD') : ''}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
          </Grid>
          <Grid item xs={12} sm={4}>
             <TextField
                label="Đến ngày"
                type="date"
                fullWidth
                value={endDate ? dayjs(endDate).format('YYYY-MM-DD') : ''}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
          </Grid>
          {userRole === 'ADMIN' && (
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={accounts}
                getOptionLabel={(option) => option.name}
                onChange={(event, newValue) => {
                  setSelectedAccount(newValue);
                }}
                renderInput={(params) => <TextField {...params} label="Chọn nhân viên" fullWidth />}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {loading && <CircularProgress />}
      {error && <Alert severity="error">Tải lịch sử điểm danh thất bại.</Alert>}
      
      {!loading && !error && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="work hours report table">
            <TableHead>
              <TableRow>
                <TableCell>Tên nhân viên</TableCell>
                <TableCell align="right">Ngày</TableCell>
                <TableCell align="right">Giờ vào</TableCell>
                <TableCell align="right">Giờ ra</TableCell>
                <TableCell align="right">Giờ công</TableCell>
                {userRole === 'ADMIN' && <TableCell align="right">Hành động</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.map((row) => (
                <TableRow key={`${row.id}`}>
                  <TableCell component="th" scope="row">
                    {row.account_name}
                  </TableCell>
                  <TableCell align="right">{dayjs(row.date).format('YYYY-MM-DD')}</TableCell>
                  <TableCell align="right">{dayjs(row.check_in_at).format('HH:mm:ss')}</TableCell>
                  <TableCell align="right">{row.check_out_at ? dayjs(row.check_out_at).format('HH:mm:ss') : 'Chưa có'}</TableCell>
                  <TableCell align="right">{typeof row.work_hours === 'number' ? row.work_hours.toFixed(2) : 'Chưa có'}</TableCell>
                  {userRole === 'ADMIN' && (
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        color="primary"
                        component={Link}
                        to={`/check-in/edit/${row.id}`}
                      >
                        Sửa
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default CheckInHistory; 